import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { WireFeedTypes } from '@src/suite/common/enums/wire-feed-types';
import { FeedProviderService } from '@src/suite/common/services/feed-providers-service';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { FeedProviderActions, FeedProviderApiActions } from '@src/suite/discover/store/actions/feed.provider.actions';
import { ApFeedRequest, ApFeedUpdateRequest, FtpFeedRequest, FtpFeedUpdateRequest, WireCredentials, WireCredentialsRequest, WireFeed } from '@src/suite/discover/store/models/feed.provider.models';
import {
    selectApCredentialsId,
    selectApFeed,
    selectApiKey,
    selectApSelectedProducts,
    selectCredentialsChanged,
    selectCredentialsTitle,
    selectCredentialsType,
    selectCurrentCredentials,
    selectFeedTitle,
    selectFeedType,
    selectFtpDirectory,
    selectFtpFeed,
    selectFtpPassword,
    selectFtpProvider,
    selectFtpUrl,
    selectFtpUsername,
    selectInEditMode,
    selectIsCredentialsDialogOpen,
    selectIsFeedDialogOpen,
} from '@src/suite/discover/store/selectors/feed.discover.selectors';
import { catchError, forkJoin, map, mergeMap, of } from 'rxjs';

@Injectable()
export class FeedProviderEffects {
    initDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.initDialog),
            mergeMap(() => {
                return [FeedProviderApiActions.getCredentialsAndFeeds(), FeedProviderApiActions.getProviders()];
            })
        );
    });

    resetDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.resetDialog),
            concatLatestFrom(() => [this.store.select(selectIsCredentialsDialogOpen), this.store.select(selectIsFeedDialogOpen)]),
            mergeMap(([_, isCredentialsDialogOpen, isFeedDialogOpen]) => {
                if (isCredentialsDialogOpen) return of(FeedProviderActions.initDialog());
                if (isFeedDialogOpen) return of(FeedProviderActions.initFeeds());
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    crudFeeds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                FeedProviderApiActions.createAPFeedSuccess,
                FeedProviderApiActions.updateAPFeedSuccess,
                FeedProviderApiActions.createFTPFeedSuccess,
                FeedProviderApiActions.updateFTPFeedSuccess,
                FeedProviderApiActions.deleteAPFeedSuccess,
                FeedProviderApiActions.deleteFtpFeedSuccess
            ),
            map(() => {
                return FeedProviderActions.initFeeds();
            })
        );
    });

    initFeeds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.initFeeds),
            map(() => {
                return FeedProviderApiActions.getCredentialsAndFeeds();
            })
        );
    });

    saveWireCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.saveWireCredentials),
            concatLatestFrom(() => this.store.select(selectInEditMode)),
            map(([_, inEditMode]) => {
                if (inEditMode) return FeedProviderApiActions.updateWireCredentials();
                return FeedProviderApiActions.createWireCredentials();
            })
        );
    });

    getWireCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getWireCredentials),
            mergeMap(action => {
                return this.feedProviderService.getWireCredentials(action.credentialsType, action.credentialsId).pipe(
                    map(res => {
                        return FeedProviderApiActions.getWireCredentialsSuccess({ credentials: res[0] });
                    }),
                    catchError(error => of(FeedProviderApiActions.getAPFeedError({ error: error })))
                );
            })
        );
    });

    saveWireFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.saveWireFeed),
            concatLatestFrom(() => [this.store.select(selectInEditMode), this.store.select(selectFeedType)]),
            map(([_, inEditMode, feedType]) => {
                if (feedType === WireFeedTypes.FTP || feedType === WireFeedTypes.SFTP) {
                    if (inEditMode) return FeedProviderApiActions.updateWireCredentials();
                    return FeedProviderApiActions.createWireCredentials();
                } else if (feedType === WireFeedTypes.AP) {
                    if (inEditMode) return FeedProviderApiActions.updateAPFeed();
                    return FeedProviderApiActions.createAPFeed();
                } else {
                    if (inEditMode) return FeedProviderApiActions.updateWireCredentials();
                    return FeedProviderApiActions.createWireCredentials();
                }
            })
        );
    });

    editWireFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.editWireFeed),
            mergeMap(action => {
                if (action.feedType === WireFeedTypes.FTP || action.feedType === WireFeedTypes.SFTP) {
                    return [
                        FeedProviderApiActions.getWireCredentials({ credentialsType: WireFeedTypes.FTP, credentialsId: action.credentialsId }),
                        FeedProviderApiActions.getFTPFeed({ feedId: action.feedId }),
                    ];
                }
                if (action.feedType === WireFeedTypes.AP) {
                    return of(FeedProviderApiActions.getAPFeed({ feedId: action.feedId }));
                }
            })
        );
    });

    getApFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getAPFeed),
            mergeMap(action => {
                return this.feedProviderService.getApFeed(action.feedId).pipe(
                    map(res => {
                        return FeedProviderApiActions.getAPFeedSuccess({ feed: res[0] });
                    }),
                    catchError(error => of(FeedProviderApiActions.getAPFeedError({ error: error })))
                );
            })
        );
    });

    getApFeedSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getAPFeedSuccess),
            map(action => {
                return FeedProviderActions.updateApCredentailsId({ apCredentialsId: action.feed.wireCredentialsId });
            })
        );
    });

    getFtpFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getFTPFeed),
            mergeMap(action => {
                return this.feedProviderService.getFTPFeed(action.feedId).pipe(
                    map(res => {
                        return FeedProviderApiActions.getFTPFeedSuccess({ feed: res[0] });
                    }),
                    catchError(error => of(FeedProviderApiActions.getFTPFeedError({ error: error })))
                );
            })
        );
    });

    createWireCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.createWireCredentials),
            concatLatestFrom(() => [
                this.store.select(selectCredentialsTitle),
                this.store.select(selectCredentialsType),
                this.store.select(selectFeedType),
                this.store.select(selectApiKey),
                this.store.select(selectFeedTitle),
                this.store.select(selectFtpUsername),
                this.store.select(selectFtpPassword),
            ]),
            mergeMap(([_, credentialsTitle, credentialsType, feedType, apiKey, feedTitle, ftpUsername, ftpPassword]) => {
                let credentials: WireCredentialsRequest;
                if (credentialsType === WireFeedTypes.AP) {
                    credentials = {
                        credentials: apiKey.trim(),
                        settings: '',
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.AP,
                        name: credentialsTitle,
                        teamId: 'USV2018',
                    };
                }
                if (credentialsType === WireFeedTypes.TWITTER) {
                    const settings = {
                        UsersByUsernameLimit: 5,
                        UserTweetsLimit: 5,
                        TweetsCapPerMonth: 10000,
                    };
                    credentials = {
                        credentials: apiKey.trim(),
                        settings: JSON.stringify(settings),
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.TWITTER,
                        name: credentialsTitle,
                        teamId: 'USV2018',
                    };
                }

                if (!credentialsType && (feedType === WireFeedTypes.FTP || feedType === WireFeedTypes.SFTP)) {
                    const creds = {
                        Username: ftpUsername,
                        Password: ftpPassword,
                    };

                    credentials = {
                        credentials: JSON.stringify(creds),
                        settings: '',
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.FTP,
                        name: feedTitle,
                        teamId: 'USV2018',
                    };
                }

                if (credentials == null) return of(ApplicationActions.noOpAction());

                return this.feedProviderService.createWireCredentials(credentials).pipe(
                    map(res => {
                        return FeedProviderApiActions.createWireCredentialsSuccess({ credentials: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.createWireCredentialsError({ error: error })))
                );
            })
        );
    });

    updateWireCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.updateWireCredentials),
            concatLatestFrom(() => [
                this.store.select(selectCurrentCredentials),
                this.store.select(selectCredentialsTitle),
                this.store.select(selectCredentialsType),
                this.store.select(selectApiKey),
                this.store.select(selectCredentialsChanged),
                this.store.select(selectFeedType),
                this.store.select(selectFeedTitle),
                this.store.select(selectFtpUsername),
                this.store.select(selectFtpPassword),
            ]),
            mergeMap(([_, cred, credTitle, credType, apiKey, credentialsChanged, feedType, feedTitle, ftpUsername, ftpPassword]) => {
                let credentials: WireCredentials;
                if (credType === WireFeedTypes.AP) {
                    credentials = {
                        wireCredentialsId: cred.wireCredentialsId,
                        wireProviderId: cred.wireProviderId,
                        teamId: cred.teamId,
                        credentials: apiKey.trim(),
                        settings: cred.settings,
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.AP,
                        name: credTitle,
                    };
                }
                if (credType === WireFeedTypes.TWITTER) {
                    const settings = {
                        UsersByUsernameLimit: 5,
                        UserTweetsLimit: 5,
                        TweetsCapPerMonth: 10000,
                    };
                    credentials = {
                        wireCredentialsId: cred.wireCredentialsId,
                        wireProviderId: cred.wireProviderId,
                        teamId: cred.teamId,
                        credentials: apiKey.trim(),
                        settings: JSON.stringify(settings),
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.TWITTER,
                        name: credTitle,
                    };
                }

                if (credType === WireFeedTypes.FTP || (!credType && (feedType === WireFeedTypes.FTP || feedType === WireFeedTypes.SFTP))) {
                    credType = WireFeedTypes.FTP;
                    const creds = {
                        Username: ftpUsername,
                        Password: ftpPassword,
                    };

                    credentials = {
                        wireCredentialsId: cred.wireCredentialsId,
                        wireProviderId: cred.wireProviderId,
                        teamId: cred.teamId,
                        credentials: JSON.stringify(creds),
                        settings: cred.settings,
                        metadata: JSON.stringify({}),
                        feedType: WireFeedTypes.FTP,
                        name: feedTitle,
                    };
                }

                if (credentials == null) return of(ApplicationActions.noOpAction());

                return this.feedProviderService.updateWireCredentials(credType, credentials, credentialsChanged).pipe(
                    map(res => {
                        return FeedProviderApiActions.updateWireCredentialsSuccess({ credentials: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.updateWireCredentialsError({ error: error })))
                );
            })
        );
    });

    crudWireCredentialsSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.createWireCredentialsSuccess, FeedProviderApiActions.updateWireCredentialsSuccess),
            concatLatestFrom(() => this.store.select(selectInEditMode)),
            mergeMap(([action, inEditMode]) => {
                if (action.credentials.feedType === WireFeedTypes.FTP) {
                    if (inEditMode) return of(FeedProviderApiActions.updateFTPFeed());
                    return of(FeedProviderApiActions.createFTPFeed({ credentialsId: action.credentials.wireCredentialsId }));
                } else {
                    return of(FeedProviderActions.initDialog());
                }
            })
        );
    });

    deleteWireCredentials$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.deleteCredentials),
            mergeMap(action => {
                return this.feedProviderService.deleteWireCredentials(action.guid, action.ftype).pipe(
                    map(() => {
                        return FeedProviderApiActions.getCredentialsAndFeeds();
                    }),
                    catchError(error => of(FeedProviderApiActions.deleteCredentialsError({ error: error })))
                );
            })
        );
    });

    getCredentialsAndFeeds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getCredentialsAndFeeds),
            mergeMap(() => {
                return forkJoin({
                    twitter: this.feedProviderService.getWireCredentials(WireFeedTypes.TWITTER),
                    ap: this.feedProviderService.getWireCredentials(WireFeedTypes.AP),
                    apfeeds: this.feedProviderService.getAPFeeds(),
                    ftpfeeds: this.feedProviderService.getFTPFeeds(),
                }).pipe(
                    map(res => {
                        const credentials = [
                            ...res.twitter.map<WireCredentials>((a: WireCredentials) => {
                                return a;
                            }),
                            ...res.ap.map<WireCredentials>((a: WireCredentials) => {
                                return a;
                            }),
                        ];

                        const feeds = [
                            ...res.apfeeds.map<WireFeed>(f => {
                                return {
                                    id: f.feedId,
                                    name: f.name,
                                    type: WireFeedTypes.AP,
                                    wireCredentialsId: f.wireCredentialsId,
                                };
                            }),
                            ...res.ftpfeeds.map<WireFeed>(f => {
                                return {
                                    id: f.feedId,
                                    name: f.name,
                                    type: WireFeedTypes.FTP,
                                    wireCredentialsId: f.wireCredentialsId,
                                };
                            }),
                        ];

                        return FeedProviderApiActions.getCredentialsAndFeedsSuccess({ credentials: credentials, feeds: feeds });
                    }),
                    catchError(error => of(FeedProviderApiActions.getCredentialsAndFeedsError({ error: error })))
                );
            })
        );
    });

    getProviders$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.getProviders),
            mergeMap(_ => {
                return this.feedProviderService.getProviders('FTP').pipe(
                    map(res => {
                        return FeedProviderApiActions.getProvidersSuccess({ providers: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.getProvidersError({ error: error })))
                );
            })
        );
    });

    createFtpFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.createFTPFeed),
            concatLatestFrom(() => [
                this.store.select(selectFeedTitle),
                this.store.select(selectFeedType),
                this.store.select(selectFtpProvider),
                this.store.select(selectFtpUrl),
                this.store.select(selectFtpDirectory),
            ]),
            mergeMap(([action, feedTitle, feedType, ftpProvider, ftpUrl, ftpDirectory]) => {
                const feed: FtpFeedRequest = {
                    url: ftpUrl,
                    directory: ftpDirectory,
                    wireCredentialsId: action.credentialsId,
                    name: feedTitle,
                    description: feedType === WireFeedTypes.FTP ? 'ftp' : 'sftp',
                    scope: 0,
                    providerId: ftpProvider,
                };

                return this.feedProviderService.createFTPFeed(feed).pipe(
                    map(res => {
                        return FeedProviderApiActions.createFTPFeedSuccess({ feed: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.createFTPFeedError({ error: error })))
                );
            })
        );
    });

    updateFtpFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.updateFTPFeed),
            concatLatestFrom(() => [
                this.store.select(selectFtpFeed),
                this.store.select(selectFeedTitle),
                this.store.select(selectFtpProvider),
                this.store.select(selectFtpUrl),
                this.store.select(selectFtpDirectory),
            ]),
            mergeMap(([_, ftpFeed, feedTitle, ftpProvider, ftpUrl, ftpDirectory]) => {
                const feed: FtpFeedUpdateRequest = {
                    feedId: ftpFeed.feedId,
                    url: ftpUrl,
                    directory: ftpDirectory,
                    name: feedTitle,
                    providerId: ftpProvider,
                    description: ftpFeed.description,
                };

                return this.feedProviderService.updateFTPFeed(feed).pipe(
                    map(res => {
                        return FeedProviderApiActions.updateFTPFeedSuccess({ feed: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.updateFTPFeedError({ error: error })))
                );
            })
        );
    });

    getApFeedProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderActions.updateApCredentailsId),
            mergeMap(action => {
                return this.feedProviderService.getApFeedProducts(action.apCredentialsId).pipe(
                    map(res => {
                        return FeedProviderApiActions.getAPFeedProductsSuccess({ products: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.getAPFeedProductsError({ error: error })))
                );
            })
        );
    });

    createApFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.createAPFeed),
            concatLatestFrom(() => [this.store.select(selectFeedTitle), this.store.select(selectApCredentialsId), this.store.select(selectApSelectedProducts)]),
            mergeMap(([_, feedTitle, apCredentialsId, apSelectedProducts]) => {
                const feed: ApFeedRequest = {
                    name: feedTitle.trim(),
                    description: '',
                    wireCredentialsId: apCredentialsId,
                    productId: apSelectedProducts.join(','),
                    // Enterprise
                    scope: 0,
                };

                return this.feedProviderService.createAPFeed(feed).pipe(
                    map(res => {
                        return FeedProviderApiActions.createAPFeedSuccess({ feed: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.createAPFeedError({ error: error })))
                );
            })
        );
    });

    updateApFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.updateAPFeed),
            concatLatestFrom(() => [this.store.select(selectApFeed), this.store.select(selectFeedTitle), this.store.select(selectApCredentialsId), this.store.select(selectApSelectedProducts)]),
            mergeMap(([_, apFeed, feedTitle, apCredentialsId, apSelectedProducts]) => {
                const feed: ApFeedUpdateRequest = {
                    feedId: apFeed.feedId,
                    name: feedTitle.trim(),
                    description: '',
                    wireCredentialsId: apCredentialsId,
                    productId: apSelectedProducts.join(','),
                };

                return this.feedProviderService.updateAPFeed(feed).pipe(
                    map(res => {
                        return FeedProviderApiActions.updateAPFeedSuccess({ feed: res });
                    }),
                    catchError(error => of(FeedProviderApiActions.updateAPFeedError({ error: error })))
                );
            })
        );
    });

    deleteFeed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeedProviderApiActions.deleteFeed),
            mergeMap(action => {
                if (action.feedType === WireFeedTypes.AP) {
                    return this.feedProviderService.deleteAPFeed(action.feedId, [0]).pipe(
                        map(res => {
                            return FeedProviderApiActions.deleteAPFeedSuccess({ feed: res });
                        }),
                        catchError(error => of(FeedProviderApiActions.deleteAPFeedError({ error: error })))
                    );
                }
                if (action.feedType === WireFeedTypes.FTP) {
                    return this.feedProviderService.deleteFTPFeed(action.feedId, [0]).pipe(
                        map(res => {
                            return FeedProviderApiActions.deleteFtpFeedSuccess({ feed: res });
                        }),
                        catchError(error => of(FeedProviderApiActions.deleteFtpFeedError({ error: error })))
                    );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    constructor(
        private actions$: Actions,
        private feedProviderService: FeedProviderService,
        private store: Store
    ) {}
}
